/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';

import EoiButtonsShared from 'bundles/degree-description/components/modules/expression-of-interest/EoiButtonsShared';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import type { EoiTextType } from 'bundles/degree-description/utils/EoiUtils';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';

type Props = {
  eoiButtons?: EoiButtonsType;
  eoiContainerText?: EoiTextType;
  blueBackground?: boolean;
  trackingIndex?: number;
  setEoiTrackingIndex?: React.Dispatch<React.SetStateAction<number>>;
};

const styles = {
  blueBackground: css`
    background-color: var(--cds-color-emphasis-primary-background-strong);
  `,
  wrapperDiv: css`
    padding-top: var(--cds-spacing-600);
    padding-bottom: var(--cds-spacing-600);
    position: relative;
    width: 100%;
  `,
  inlineContainer: css`
    margin: 0 auto;
    max-width: 1118px;
    align-items: center;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 0 40px -8px rgb(0 0 0 / 32%);
    padding: 25px 0 26px;
    position: relative;
    width: 100%;
  `,
  textWrap: css`
    align-items: center;
    padding-left: var(--cds-spacing-400);
    padding-right: var(--cds-spacing-200);
    width: 70%;

    ${breakpoints.down('md')} {
      width: 60%;
    }
    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-200);
      padding-left: var(--cds-spacing-300);
      padding-right: var(--cds-spacing-300);
      width: 100%;
    }
  `,
  buttonWrap: css`
    align-content: space-between;
    align-items: center;
    justify-content: flex-end;
    margin: 0;
    padding-right: var(--cds-spacing-200);
    width: 30%;

    .rc-ApplyButton,
    .rc-EmailMeButton {
      margin-bottom: 0;
      padding-left: 0;
      padding-right: 0;

      span {
        margin: 0 auto;
        padding: 0 16px;
      }
    }

    ${breakpoints.down('md')} {
      width: 40%;
    }
    ${breakpoints.down('xs')} {
      padding-left: var(--cds-spacing-200);
      width: 100%;
    }
  `,
  buttonItem: css`
    min-width: 50%;

    ${breakpoints.down('sm')} {
      width: 100%;
    }
    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
};

export const EoiInlineModule = ({
  eoiButtons,
  eoiContainerText,
  blueBackground,
  trackingIndex,
  setEoiTrackingIndex,
}: Props) => {
  const theme = useDegreeDescriptionPageTheme();
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageSectionRef = useGetViewPageSectionRef('eoi', trackingIndex);

  if (!eoiContainerText || !eoiButtons) return null;

  const { inlineTitle, inlineDescription } = eoiContainerText;

  if (!inlineTitle || !inlineDescription) return null;

  return (
    <div css={[styles.wrapperDiv, blueBackground && styles.blueBackground]} ref={pageSectionRef}>
      <Grid container css={styles.inlineContainer}>
        <Grid css={styles.textWrap} container item className="eoi-header-text">
          <Grid item>
            <Typography2 variant="subtitleLarge" component="h3">
              {inlineTitle}
            </Typography2>
            <Typography2 component="p" variant="bodyPrimary" css={theme.mobileFontSizeStyle?.body}>
              {inlineDescription}
            </Typography2>
          </Grid>
        </Grid>
        <Grid container item spacing={16} css={styles.buttonWrap}>
          <EoiButtonsShared
            keySuffix="-inline-eoi-button"
            eoiButtons={eoiButtons}
            eoiContainerText={eoiContainerText}
            trackingNamePrefix="inline"
            trackingIndex={1}
            setEoiTrackingIndex={setEoiTrackingIndex}
            enableGrid
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default EoiInlineModule;
